import { Vector3 } from "three";
import { isArray, isDefined, MultiPointNode } from "../../infra3d";
import { Geometry, GEOMETRYTYPE, PickData } from "./internal";
/**
 * Representation of the FeatureCollection-Object specified in GeoJSON-Standard.
 *
 * @class MultiPointGeometry
 * @extends Geometry
 */
export class MultiPointGeometry extends Geometry {
    constructor(parentfeature) {
        super(parentfeature);
        this.type = GEOMETRYTYPE.MULTIPOINT;
        this.parent = parentfeature;
        this.scene = parentfeature.scene;
    }
    /**
     * Parse the Geometry from a JSON-Object
     * @method parseFromJSON
     * @param {Object} json The JSON Object
     */
    parseFromJSON(json) {
        if (isDefined(json["coordinates"])) {
            this.type = Geometry.toNumber(json["type"]);
            const ls = json["coordinates"];
            //Check if it is a valid coordinate-Array
            if (isArray(ls)) {
                this.coordinates = json["coordinates"];
                this._create();
            }
        }
        else {
            throw "Error: Coordinate array not defined.";
        }
    }
    _create() {
        if (this.coordinates.length > 0) {
            this.node = new MultiPointNode(this.coordinates, this.parent.style.toMaterialDefinitions(this.parent.properties), this);
            this.scene.add(this.node);
            this.created = true;
        }
    }
    clear() {
        this.created = false;
        if (this.node)
            this.scene.remove(this.node);
        this.coordinates = [];
    }
    /**
     * returns the Geometry content as GeoJson string
     * @method toGeoJsonString
     * @return {string}
     */
    toGeoJsonString() {
        const objparts = {};
        objparts["type"] = Geometry.toString(this.type);
        objparts["coordinates"] = this.coordinates;
        return JSON.stringify(objparts);
    }
    /**
     * Removes the Geometry from the Render-Engine and destroys it.
     * @method destroy
     */
    destroy() {
        this.coordinates[0] = [];
        this.type = null;
        if (this.created) {
            this.scene.remove(this.node);
            this.node.destroy();
        }
        this.id = null;
        this.created = false;
    }
    /**
     * Shows the geometry in Higlight-Style
     */
    highlightOn() {
        if (this.created) {
            this.node.setStyle(this.parent.highlightstyle.toMaterialDefinitions(this.parent.properties));
        }
    }
    /**
     * Shows the geometry in Higlight-Style
     */
    highlightOff() {
        if (this.created) {
            this.node.setStyle(this.parent.style.toMaterialDefinitions(this.parent.properties));
        }
    }
    movePoint(newcoord, index) {
        if (this.created) {
            this.coordinates[index] = newcoord;
            this.update();
        }
    }
    /**
     * Insert a point in the geomtery at a certain position
     * @method insertPoint
     * @param {number[]} newcoord
     * @param {number} index
     */
    insertPoint(newcoord, index) {
        if (index >= 0 && index < this.coordinates.length) {
            this.coordinates.splice(index, 0, newcoord);
            this.update();
        }
    }
    /**
     * Delete a point in the geomtery at a certain position
     * @method deletePoint
     * @param {number} index
     */
    deletePoint(index) {
        if (index >= 0 && index < this.coordinates.length) {
            this.coordinates.splice(index, 1);
            this.update();
        }
    }
    update() {
        if (!this.created) {
            this._create();
        }
        else {
            if (!this.parent.highlighton) {
                this.node.setStyle(this.parent.style.toMaterialDefinitions(this.parent.properties));
            }
            else {
                this.node.setStyle(this.parent.highlightstyle.toMaterialDefinitions(this.parent.properties));
            }
            this.node.updateCoordinates(this.coordinates);
        }
    }
    /**
     * Shows the Geometry. It sets hide to false and updates the geometry.
     * @method show
     */
    show() {
        if (this.created)
            this.node.show();
    }
    /**
     * Hides the Geometry. It sets hide to true and updates the geometry.
     * @method hide
     */
    hide() {
        if (this.created)
            this.node.hide();
    }
    /**
     * Checks if the node was picked by mouse
     * @method hide
     */
    pick(mx, my, pickeddataarray) {
        if (this.created) {
            const p = this.node.pick(mx, my);
            if (p.length > 0) {
                if (p.length > 1) {
                    //sort by distanceToRay -> THREE.js sorts by distance by default.
                    p.sort(function (a, b) {
                        return a.distanceToRay - b.distanceToRay;
                    });
                }
                const foundmouse = this.scene
                    .getMeasureHandler()
                    .worldToMouse(new Vector3(this.node.coordinates[p[0].index][0], this.node.coordinates[p[0].index][1], this.node.coordinates[p[0].index][2]));
                const dx = foundmouse.x - mx;
                const dy = foundmouse.y - my;
                const dist = Math.sqrt(dx * dx + dy * dy);
                //threshold between 10 and 100 pixel.
                let pick_threshold = 10;
                if (this.node.materialDefinition)
                    if (this.node.materialDefinition.stroke)
                        pick_threshold = Math.min(Math.max(this.node.materialDefinition.stroke.size / 2, 10), 100);
                if (dist < pick_threshold) {
                    const pdata = new PickData();
                    pdata.feature = this.parentfeature;
                    pdata.mindist = p[0].distanceToRay;
                    pdata.mx = mx;
                    pdata.my = my;
                    pdata.found = true;
                    pdata.disttopoint = p[0].distanceToRay;
                    pdata.found_mx = foundmouse.x;
                    pdata.found_my = foundmouse.y;
                    pdata.geometry = this;
                    pdata.found_easting = this.node.coordinates[p[0].index][0];
                    pdata.found_northing = this.node.coordinates[p[0].index][1];
                    pdata.found_height = this.node.coordinates[p[0].index][2];
                    pdata.pointindex = p[0].index;
                    pickeddataarray.push(pdata);
                }
            }
        }
    }
}
//# sourceMappingURL=multipointgeometry.js.map